import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as TransactionIcon } from '../../assets/icons/sidebar/sidebar-payment.svg';
import DefaultApiLimit from '../../config/defaultLimit';
import { PortalType } from '../../config/enum';
import { usePortal } from '../../contexts/PortalContext/PortalContext';
import { CDRTransactionInterface } from '../../interface/payment-management.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../redux/slices/PageTitleSlice';
import {
  disablePaymentManagementApplied,
  fetchAllCDRTransactions,
  // fetchListPaymentMethodFilters,
  updatePaymentManagementFilters,
  setSortData,
} from '../../redux/slices/PaymentManagementSlice';
import { setSidePanelData } from '../../redux/slices/TableSidePanelSlice';
import CustomDateRangePicker from '../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import CpoCdrSidePanel from '../../shared-components/table-side-panel/CpoCdrSidePanel';
import DeviceSidePanel from '../../shared-components/table-side-panel/DeviceSidePanel';
import FacilityDetailsSidePanel from '../../shared-components/table-side-panel/FacilityDetailsSidePanel';
import { useTableSidePanel } from '../../shared-components/table-side-panel/hooks/useTableSidePanel';
import LocationSidePanel from '../../shared-components/table-side-panel/LocationSidePanel';
import TariffSidePanel from '../../shared-components/table-side-panel/TariffSidePanel';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../shared-functions/ConvertUTCtoDateTime';
import { getDaysBetween, removeEmptyKeys, changeDateFormat } from '../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../shared-functions/ExportFile';
import FormatDuration from '../../shared-functions/FormatDuration';
import { permissionCheck } from '../../shared-functions/JWTDocodeFunction';
import ToFixedValue from '../../shared-functions/ToFixedValue';
import cpoTransactionApi from '../../utils/api/cpo-transaction';

/**
 * Payments Management function Component
 * @returns {any} The rendered CPOTransactionManagement component
 */
function CPOTransactionManagement() {
  const { t } = useTranslation('transaction');
  const dispatch = useAppDispatch();
  const { portalType } = usePortal();
  const isPartnerPortal = portalType === PortalType.PARTNER;
  const {
    payments,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.payments);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [searchParameters, setSearchParameters] = useSearchParams();

  const currentDate = new Date();
  const pastDate = new Date();
  pastDate.setDate(currentDate.getDate() - 13);

  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: changeDateFormat(pastDate.toString()),
    endDate: changeDateFormat(currentDate.toString()),
  });

  /**
   * Clears the date range values
   */
  const clearDateRange = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
  };

  /**
   * Render the date filters
   * @returns {any} The result
   */
  const renderDateFilters = () => {
    return (
      <div className="date-content">
        <CustomDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
          placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          defaultPreset="last14Days"
          disableDates="future"
        />
      </div>
    );
  };

  /**
   * Fetch the payment details in redux
   * @param {any} data - The query payload for fetching transactions
   */
  const fetchPayments = (data: any) => {
    if (data.startDate && data.endDate && getDaysBetween(data.startDate, data.endDate) > 31) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitMessage'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }

    if (!loading) {
      dispatch(fetchAllCDRTransactions(data));
    }
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {}; // no sort if empty
    fetchPayments({
      page,
      limit,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      ...queryParameters,
      ...sortValue,
    });
  };

  const { isEditMode, handleEditModeChange, handleSidePanelClose, renderSidePanelTopLeftContent } =
    useTableSidePanel({
      onRefresh: handleRefresh,
      sessions: payments,
    });

  const column = [
    {
      label: t('CDR ID'),
      accessorKey: 'cdrId' as keyof CDRTransactionInterface,
      queryParamsKey: 'transactionObjectId',
      queryParamsValue: '_id',
      sidePanelComponent: <CpoCdrSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: t('ocpiCDRID'),
      accessorKey: 'ocpiId' as keyof CDRTransactionInterface,
    },
    {
      label: t('Session ID'),
      accessorKey: 'sessionId' as keyof CDRTransactionInterface,
      queryParamsKey: 'transactionObjectId',
    },
    {
      label: t('OCPPTransactionid'),
      accessorKey: 'transactionId' as keyof CDRTransactionInterface,
    },
    {
      label: t('Account ID'),
      accessorKey: 'accountCustomId' as keyof CDRTransactionInterface,
      sortAllow: true,
      ...(!isPartnerPortal
        ? {
            queryParamsKey: 'accountCustomId',
            queryParamsValue: 'accountCustomId',
            sidePanelComponent: (
              <FacilityDetailsSidePanel
                actionActive
                isEditMode={isEditMode}
                onEditModeChange={handleEditModeChange}
                fetchIdFrom="queryParams"
                onSuccess={() => {
                  handleEditModeChange(false);
                  handleRefresh();
                }}
                onRefreshList={handleRefresh}
              />
            ),
            disableNewTab: true,
          }
        : {}),
    },
    {
      label: t('Charging Point'),
      accessorKey: 'chargingPointSn' as keyof CDRTransactionInterface,
      sortAllow: false,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: (
        <DeviceSidePanel
          fetchIdFrom="queryParams"
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      disableNewTab: true,
    },
    {
      label: t('Location ID'),
      accessorKey: 'cdrLocation' as keyof CDRTransactionInterface,
      sortAllow: false,
      queryParamsKey: 'stationId',
      queryParamsValue: 'id',
      sidePanelComponent: (
        <LocationSidePanel
          fetchIdFrom="queryParams"
          initialIsEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      disableNewTab: true,
      /**
       * Location ID custom formatter
       * @param {CDRTransactionInterface} row - Row data
       * @returns {any} Location ID element
       */
      customData: (row: CDRTransactionInterface) => (
        <span
          className="table-row-each-data hyperlink"
          role="presentation"
          onClick={(event) => {
            event.stopPropagation();
            if (!row.cdrLocation?.id) return;
            const parameters = new URLSearchParams(searchParameters);
            const sidePanelKeys = [
              'accountCustomId',
              'deviceId',
              'connectorDeviceId',
              'stationId',
              'facilityId',
              'userCustomId',
              'userId',
              'utilityId',
              'transactionObjectId',
              'roleId',
              'sessionId',
              'cdrObjectId',
              'paymentDetailsId',
              'personalChargerId',
              'action',
            ];
            sidePanelKeys.forEach((key) => parameters.delete(key));
            parameters.set('stationId', row.cdrLocation.id);
            setSearchParameters(parameters);
            dispatch(
              setSidePanelData({
                key: 'Transactions',
                value: row.id,
              }),
            );
          }}>
          {row.cdrLocation?.id || '-'}
        </span>
      ),
    },
    {
      label: t('EVSE UID'),
      accessorKey: 'evseUid' as keyof CDRTransactionInterface,
      sortAllow: false,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: (
        <DeviceSidePanel
          fetchIdFrom="queryParams"
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      disableNewTab: true,
      /**
       * EVSE UID custom formatter
       * @param {CDRTransactionInterface} row - Row data
       * @returns {any} EVSE UID element
       */
      customData: (row: CDRTransactionInterface) => (
        <span
          className="table-row-each-data hyperlink"
          role="presentation"
          onClick={(event) => {
            event.stopPropagation();
            if (!row.chargingPointSn) return;
            const parameters = new URLSearchParams(searchParameters);
            const sidePanelKeys = [
              'accountCustomId',
              'deviceId',
              'connectorDeviceId',
              'stationId',
              'facilityId',
              'userCustomId',
              'userId',
              'utilityId',
              'transactionObjectId',
              'roleId',
              'sessionId',
              'cdrObjectId',
              'paymentDetailsId',
              'personalChargerId',
              'action',
            ];
            sidePanelKeys.forEach((key) => parameters.delete(key));
            parameters.set('deviceId', row.chargingPointSn);
            setSearchParameters(parameters);
            dispatch(
              setSidePanelData({
                key: 'Transactions',
                value: row.id,
              }),
            );
          }}>
          {row.cdrLocation?.evseUid || '-'}
        </span>
      ),
    },
    {
      label: t('connectorId'),
      accessorKey: 'connectorId' as keyof CDRTransactionInterface,
      /**
       * Connector ID custom formatter
       * @param {CDRTransactionInterface} row - Row data
       * @returns {any} Connector ID element
       */
      customData: (row: CDRTransactionInterface) => (
        <span>{row.cdrLocation?.connectorId || '-'}</span>
      ),
    },
    {
      label: t('tariffName'),
      accessorKey: 'tariffs' as keyof CDRTransactionInterface,
      sortAllow: false,
      queryParamsKey: 'tariffId',
      sidePanelComponent: <TariffSidePanel fetchIdFrom="queryParams" isPartner={false} />,
      disableNewTab: true,
      /**
       * Tariff name custom formatter
       * @param {CDRTransactionInterface} row - Row data
       * @returns {any} Tariff name element
       */
      customData: (row: CDRTransactionInterface) => {
        const tariff = row.tariffs?.[0];
        const tariffId = tariff?.tariffId || tariff?._id;
        const tariffName = tariff?.name || '-';

        if (!tariffId || tariffName === '-') {
          return <span>{tariffName}</span>;
        }

        return (
          <span
            className="table-row-each-data hyperlink"
            role="presentation"
            onClick={(event) => {
              event.stopPropagation();
              const parameters = new URLSearchParams(searchParameters);
              const sidePanelKeys = [
                'accountCustomId',
                'deviceId',
                'connectorDeviceId',
                'stationId',
                'facilityId',
                'userCustomId',
                'userId',
                'utilityId',
                'transactionObjectId',
                'roleId',
                'sessionId',
                'cdrObjectId',
                'paymentDetailsId',
                'personalChargerId',
                'tariffId',
                'action',
              ];
              sidePanelKeys.forEach((key) => parameters.delete(key));
              parameters.set('tariffId', tariffId);
              setSearchParameters(parameters);
              dispatch(
                setSidePanelData({
                  key: 'Transactions',
                  value: row.id,
                }),
              );
            }}>
            {tariffName}
          </span>
        );
      },
    },
    {
      label: t('Energy (kWh)'),
      accessorKey: 'totalEnergy' as keyof CDRTransactionInterface,
      /**
       * Energy custom formatter
       * @param {CDRTransactionInterface} row - Row data
       * @returns {any} Energy element
       */
      customData: (row: CDRTransactionInterface) => (
        <span>{ToFixedValue(row.totalEnergy, 3) || '0.000'} kWh</span>
      ),
    },
    {
      label: t('Total Cost'),
      accessorKey: 'totalCost' as keyof CDRTransactionInterface,
      /**
       * Total cost custom formatter
       * @param {CDRTransactionInterface} row - Row data
       * @returns {any} Formatted total cost element
       */
      customData: (row: CDRTransactionInterface) => (
        <span>
          {row.totalCost?.inclVat !== undefined ? ToFixedValue(row.totalCost.inclVat) : '0.00'}{' '}
          {row.currency}
        </span>
      ),
    },
    {
      label: t('Total Time'),
      accessorKey: 'totalTime' as keyof CDRTransactionInterface,
      /**
       * Render duration in formatted string.
       * @param {CDRTransactionInterface} rowData - The row data.
       * @returns {any} The total duration element.
       */
      customData: (rowData: CDRTransactionInterface): React.ReactElement => (
        <span>{FormatDuration(rowData.totalTime)}</span>
      ),
    },
    {
      label: t('Created'),
      accessorKey: 'createdAt' as keyof CDRTransactionInterface,
      /**
       * Render duration in formatted string.
       * @param {CDRTransactionInterface} rowData - The row data.
       * @returns {any} The total duration element.
       */
      customData: (rowData: CDRTransactionInterface) => (
        <span>{ConvertUTCtoDateTime(rowData.createdAt)}</span>
      ),
    },
    {
      label: t('Last Updated'),
      accessorKey: 'updatedAt' as keyof CDRTransactionInterface,
      /**
       * Render duration in formatted string.
       * @param {CDRTransactionInterface} rowData - The row data.
       * @returns {any} The total duration element.
       */
      customData: (rowData: CDRTransactionInterface) => (
        <span>{ConvertUTCtoDateTime(rowData.updatedAt)}</span>
      ),
    },
  ];

  const [exportLoader, setExportLoader] = useState<boolean>(false);

  /**
   * Column definitions for the table export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('CDR ID'), accessorKey: 'cdrId' },
    { label: t('ocpiCDRID'), accessorKey: 'ocpiId' },
    { label: t('Session ID'), accessorKey: 'sessionId' },
    { label: t('OCPPTransactionid'), accessorKey: 'transactionId' },
    { label: t('Account ID'), accessorKey: 'accountCustomId' },
    { label: t('Charging Point'), accessorKey: 'chargingPointSn' },
    { label: t('Location ID'), accessorKey: 'cdrLocation' },
    { label: t('EVSE UID'), accessorKey: 'evseUid' },
    { label: t('connectorId'), accessorKey: 'connectorId' },
    { label: t('tariffName'), accessorKey: 'tariffs' },
    { label: t('energyInkWh'), accessorKey: 'totalEnergy' },
    { label: t('Total Cost'), accessorKey: 'totalCost' },
    { label: t('Total Time'), accessorKey: 'totalTime' },
    { label: t('Created'), accessorKey: 'createdAt' },
    { label: t('Last Updated'), accessorKey: 'updatedAt' },
  ];

  /**
   * Table pagination function
   * @param {any} value - Pagination value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {}; // no sort if empty
    fetchPayments({
      ...value,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      ...queryParameters,
      ...sortValue,
    });
  };

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   * @param {string} type - The export format type ('csv' or 'pdf')
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload: Record<string, any> = {
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;
    const apiCall = cpoTransactionApi.fetchCpoTransactionsExport(removeEmptyKeys(payload));

    apiCall
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: CDRTransactionInterface, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'cdrId':
                return row.cdrId || t('na');
              case 'ocpiId':
                return row.ocpiId || t('na');
              case 'sessionId':
                return row.sessionId || t('na');
              case 'transactionId':
                return row.transactionId || t('na');
              case 'accountCustomId':
                return row.accountCustomId || t('na');
              case 'chargingPointSn':
                return row.chargingPointSn || t('na');
              case 'cdrLocation':
                return row.cdrLocation?.id || '-';
              case 'evseUid':
                return row.cdrLocation?.evseUid || '-';
              case 'connectorId':
                return row.cdrLocation?.connectorId || '-';
              case 'tariffs':
                return row.tariffs?.[0]?.name || '-';
              case 'totalEnergy':
                return ToFixedValue(row.totalEnergy) ?? '0.00';
              case 'totalCost':
                return row.totalCost?.inclVat !== undefined
                  ? `${ToFixedValue(row.totalCost.inclVat)} ${row.currency}`
                  : `0.00 ${row.currency ?? ''}`;
              case 'totalTime':
                return FormatDuration(row.totalTime);
              case 'createdAt':
                return ConvertUTCtoDateTime(row.createdAt);
              case 'updatedAt':
                return ConvertUTCtoDateTime(row.updatedAt);
              default:
                return (row as any)[col.accessorKey] || t('na');
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'payments-list.csv');
          } else {
            exportPDF(headers, rows, 'payments-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData object containing sortField and sortBy
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchPayments({
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  const isFirstMountFilters = React.useRef(true);

  useEffect(() => {
    if (isFirstMountFilters.current) {
      isFirstMountFilters.current = false;
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchPayments({
        page: DefaultApiLimit.page,
        limit,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        ...queryParameters,
      });
      return;
    }

    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchPayments({
      page: DefaultApiLimit.page,
      limit,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      ...queryParameters,
    });

    if (isFilterUpdated) {
      dispatch(disablePaymentManagementApplied());
    }
  }, [filters, dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('transactions'),
        icon: <TransactionIcon />,
        count: totalResults,
      }),
    );
  }, [totalResults, t]);

  useEffect(() => {
    if (
      searchParameters.get('deviceId') ||
      searchParameters.get('userCustomId') ||
      searchParameters.get('transactionObjectId')
    ) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('deviceId');
      parameters.delete('userCustomId');
      parameters.delete('transactionObjectId');
      setSearchParameters(parameters, { replace: true });
    }
  }, []);

  return (
    <div className="payment-management-wrap">
      <div className="table-content">
        <CustomTable
          dataKey="Transactions"
          version="v2"
          header={column}
          data={payments}
          limit={limit}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          loading={loading}
          serialNo
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          pageLimitChange={pageNateLimitChange}
          onHiddenColumnsChange={setHiddenColumns}
          sizeChangeButtonRequired
          filters={filters}
          filtersToDispatch={updatePaymentManagementFilters}
          filterListLoading={filterListLoading}
          // filterListToDispatch={fetchListPaymentMethodFilters}
          refreshRequired
          handleRefresh={handleRefresh}
          refershCurrentPages={handleRefresh}
          exportDataPdfOrCSV={
            permissionCheck('cpoTransactionExport') || permissionCheck('partnerTransactionExport')
              ? exportDataPdfOrCSV
              : undefined
          }
          exportLoader={exportLoader}
          hoverLabel={t('refresh')}
          rightTopContent={renderDateFilters()}
          onClearFilterChange={() => {
            clearDateRange();
          }}
          sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
          onSidePanelClose={handleSidePanelClose}
        />
      </div>
    </div>
  );
}

export default CPOTransactionManagement;
